import * as React from "react";

// Puerto JSX del componente tilted-grid-hero (shadcn-style) para este proyecto CRA.
// Banda de imágenes curvada en 3D: una fila fluye de derecha a izquierda por
// dentro de un cilindro; los extremos se curvan hacia el espectador y se desvanecen.
// Variante `flat`: franja plana continua (ideal para pantallas pequeñas).
const cn = (...a) => a.filter(Boolean).join(" ");

const SLICES = 16;
const CAMERA = 1.6;

function arc(bend) {
  return (CAMERA - 1 + Math.cos(bend)) / (2 * CAMERA * Math.sin(bend));
}

const MAX_WIDTH = 55;

function measure(width, height, tile, aspect, gap, bend) {
  const h = Math.min((tile / 100) * height, ((MAX_WIDTH / 100) * width) / aspect);
  const radius = width * arc(bend);
  if (!(h > 0) || !(radius > 0)) return { columns: 2, sweep: 0, unit: 0, limit: 0 };
  const deg = (rad) => (rad * 180) / Math.PI;
  const unit = deg(h / radius);
  const pitch = (aspect + gap / 100) * unit;
  const limit = deg(bend) + (aspect * unit) / 2;
  const columns = Math.min(60, Math.max(2, Math.ceil((2 * limit) / pitch)));
  const round = (n) => +n.toFixed(4);
  const sweep = round((columns * pitch) / 2);
  return { columns, sweep, unit: round(unit), limit: round(Math.min(sweep, limit)) };
}

export function TiltedGridHero({
  images,
  speed = 4,
  tileHeight = 26,
  aspectRatio = 16 / 9,
  gap = 6,
  axis = 56,
  curve = 80,
  fade = 12,
  flat = false,
  children,
  className,
  ...props
}) {
  const ref = React.useRef(null);
  const id = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const orbit = `tgh-o-${id}`;
  const tile = `tgh-t-${id}`;
  const bend = (Math.min(85, Math.max(5, curve)) * Math.PI) / 180;

  const [layout, setLayout] = React.useState(null);
  const [shown, setShown] = React.useState({});

  // La franja (3D o plana) se dibuja solo tras cargar, para que el primer render
  // coincida siempre con el HTML prerenderizado (evita el error de hidratación #418).
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const next = measure(width, height, tileHeight, aspectRatio, gap, bend);
      setLayout((prev) =>
        prev?.columns === next.columns && prev.sweep === next.sweep && prev.unit === next.unit ? prev : next
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [tileHeight, aspectRatio, gap, bend]);

  React.useEffect(() => {
    for (const { src } of images) new Image().src = src;
  }, [images]);

  const { columns, sweep, unit, limit } = layout ?? measure(1200, 560, tileHeight, aspectRatio, gap, bend);

  const u = (n) =>
    `calc(${+n.toFixed(4)} * min(${tileHeight}cqh, ${+(MAX_WIDTH / aspectRatio).toFixed(4)}cqw))`;
  const r = 100 * arc(bend);
  const radius = `${+r.toFixed(3)}cqw`;

  const turn = (deg) =>
    `translateZ(${radius}) rotateY(${+deg.toFixed(4)}deg) translateZ(-${radius})`;

  const hide = +(((sweep - limit) / (2 * sweep || 1)) * 100).toFixed(4);

  const css =
    `@keyframes ${orbit}{` +
    `from{transform:${turn(-sweep)}}to{transform:${turn(sweep)}}` +
    `0%,${hide}%,${100 - hide}%,100%{visibility:hidden}` +
    `${hide + 0.001}%,${100 - hide - 0.001}%{visibility:visible}}` +
    `@media(prefers-reduced-motion:reduce){.${tile}{animation-play-state:paused}}`;

  const share = aspectRatio / SLICES;
  const duration = columns * speed;

  const flatImages = [...images, ...images];

  return (
    <div
      ref={ref}
      className={cn("tgh-container relative overflow-hidden", className)}
      {...props}
      style={{ ...props.style }}
    >
      <style>{css}</style>
      {mounted ? (flat ? (
        <div aria-hidden className="tgh-flat pointer-events-none absolute inset-0">
          <div className="tgh-flat-track">
            {flatImages.map((img, i) => (
              <img
                key={i}
                src={img.src}
                srcSet={img.srcset}
                sizes={img.sizes}
                alt=""
                width={img.w}
                height={img.h}
                loading={i === 0 ? "eager" : "lazy"}
                draggable={false}
                className="tgh-flat-img"
              />
            ))}
          </div>
        </div>
      ) : (
        <div
          aria-hidden
          className="tgh-band pointer-events-none absolute inset-0 transition-opacity duration-500"
          style={{
            opacity: layout ? 1 : 0,
            perspective: `${+(r * CAMERA).toFixed(3)}cqw`,
            perspectiveOrigin: `50% ${axis}%`,
          }}
        >
          {Array.from({ length: columns }, (_, t) => {
            const first = columns - 1 - t;
            const img = images[(shown[t] ?? first) % Math.max(images.length, 1)];
            return (
              <div
                key={t}
                className={cn(tile, "absolute")}
                style={{
                  left: `calc(50% - ${u(aspectRatio / 2)})`,
                  top: `calc(${axis}% - ${u(0.5)})`,
                  width: u(aspectRatio),
                  height: u(1),
                  transformStyle: "preserve-3d",
                  animation: `${orbit} ${duration}s linear ${-t * speed}s infinite`,
                }}
                onAnimationIteration={(e) => {
                  const lap = Math.round(e.elapsedTime / duration);
                  setShown((prev) => {
                    const next = lap * columns + first;
                    return prev[t] === next ? prev : { ...prev, [t]: next };
                  });
                }}
              >
                {Array.from({ length: SLICES }, (_, k) => (
                  <div
                    key={k}
                    className={cn("absolute top-0 overflow-hidden", k === 0 && "rounded-l-lg", k === SLICES - 1 && "rounded-r-lg")}
                    style={{
                      left: u((aspectRatio - share) / 2),
                      width: k === SLICES - 1 ? u(share) : `calc(${u(share)} + 1px)`,
                      height: u(1),
                      backgroundColor: "#252422",
                      transform: turn((aspectRatio / 2 - (k + 0.5) * share) * unit),
                    }}
                  >
                    {img ? (
                      <img
                        src={img.src}
                        srcSet={img.srcset}
                        sizes="448px"
                        alt=""
                        width={img.w}
                        height={img.h}
                        loading={img.src === images[0].src ? "eager" : "lazy"}
                        draggable={false}
                        className="absolute top-0 max-w-none object-cover"
                        style={{ left: u(-k * share), width: u(aspectRatio), height: u(1), objectPosition: img.op || "50% 50%" }}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )) : null}
      {children}
    </div>
  );
}

export default TiltedGridHero;
